import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "./firebase"; 
import { doc, getDoc } from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";

const LoginForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // NEW: State for tracking hover on the register link
  const [isLinkHovered, setIsLinkHovered] = useState(false);

  // Orange theme colors
  const primaryOrange = "#FF6B00";
  const darkOrange = "#E66000";
  const borderColor = "#E5E7EB";

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const userData = docSnap.data();
        
        if (userData.role === "admin") {
          navigate("/dashboard");
        } else {
          navigate("/userDashboard");
        }
      } else {
        setError("User profile not found.");
      }
      
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Invalid email or password.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Try again later.");
      } else {
        setError("Failed to log in. Please try again.");
      }
    }
    setLoading(false);
  };

  const isButtonDisabled = loading || !email || !password;

  // --- Styles ---
  const containerStyle = {
    maxWidth: "420px",
    margin: "40px auto",
    padding: "40px",
    borderRadius: "24px",
    backgroundColor: "white",
    boxShadow: "0 15px 35px rgba(255, 107, 0, 0.08)",
    textAlign: "center",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    border: `2px solid ${primaryOrange}`,
  };

  const inputStyle = {
    width: "100%",
    padding: "15px",
    margin: "10px 0",
    border: "none",
    borderBottom: `2px solid ${borderColor}`,
    outline: "none",
    fontSize: "15px",
    boxSizing: "border-box",
    transition: "border-color 0.3s",
    color: "#333",
    backgroundColor: "transparent"
  };

  const EyeIcon = ({ visible }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={primaryOrange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {visible ? (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </>
      )}
    </svg>
  );

  return (
    <div style={containerStyle}>
      <h2 style={{ color: "#1F2937", margin: "0 0 8px 0", fontSize: "28px", fontWeight: "800" }}>
        Welcome <span style={{ color: primaryOrange }}>Back</span>
      </h2>
      <p style={{color: "#6B7280", margin: "0 0 30px 0", fontSize: "14px"}}>Sign in to continue your journey</p>

      <form onSubmit={handleLogin} style={{display: "flex", flexDirection: "column"}}>
        <input 
          type="email" 
          placeholder="Email address" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          style={inputStyle} 
        />
        
        <div style={{ position: "relative" }}>
          <input 
            type={showPassword ? "text" : "password"} 
            placeholder="Password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            style={{...inputStyle, paddingRight: "40px"}} 
          />
          <span 
            onClick={() => setShowPassword(!showPassword)} 
            style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", zIndex: 10, display: "flex", alignItems: "center" }}
          >
            <EyeIcon visible={showPassword} />
          </span>
        </div>

        {error && <div style={{ color: "#EF4444", fontSize: "13px", backgroundColor: "#FEE2E2", padding: "10px", borderRadius: "8px", margin: "15px 0" }}>{error}</div>}

        <button 
          type="submit"
          disabled={isButtonDisabled}
          style={{ 
            width: "100%", 
            padding: "16px", 
            background: isButtonDisabled 
                ? "#E5E7EB" 
                : `linear-gradient(135deg, ${primaryOrange} 0%, ${darkOrange} 100%)`, 
            color: isButtonDisabled ? "#9CA3AF" : "white", 
            border: "none", 
            borderRadius: "12px", 
            cursor: isButtonDisabled ? "not-allowed" : "pointer", 
            fontWeight: "700", 
            fontSize: "16px", 
            marginTop: "10px",
            transition: "all 0.3s ease",
            boxShadow: isButtonDisabled ? "none" : "0 4px 12px rgba(255, 107, 0, 0.3)",
          }}
        >
          {loading ? "Signing In..." : "SIGN IN"}
        </button>
      </form>

      {/* --- OR Divider --- */}
      <div style={{ display: "flex", alignItems: "center", margin: "25px 0", color: "#9CA3AF", fontSize: "13px" }}>
        <div style={{ flex: 1, height: "1px", backgroundColor: borderColor }} />
        <span style={{ margin: "0 10px", fontWeight: "500" }}>OR</span>
        <div style={{ flex: 1, height: "1px", backgroundColor: borderColor }} />
      </div>

      <div style={{ marginTop: "20px", fontSize: "14px", color: "#4B5563" }}>
        Don't have an account?{" "}
        <Link 
            to="/" 
            style={{ 
                color: primaryOrange, 
                fontWeight: "700", 
                textDecoration: isLinkHovered ? "underline" : "none", // Dynamic Underline
                transition: "text-decoration 0.2s"
            }}
            // Added mouse events to track hover
            onMouseEnter={() => setIsLinkHovered(true)}
            onMouseLeave={() => setIsLinkHovered(false)}
        >
          Register
        </Link>
      </div>
    </div>
  );
};

export default LoginForm;